import { useState } from "react";
import { toast } from "sonner";
import { Download, Undo2, Gift, Layers, RefreshCw, Plus } from "lucide-react";
import { api, API, formatDate, formatPrice } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { ADMIN } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Th, Td, CwStatusBadge } from "@/components/admin/shared/UIPrimitives";
import RefundDialog from "@/components/admin/RefundDialog";
import ConsolidateInvoicesDialog from "@/components/admin/ConsolidateInvoicesDialog";
import LineItemsDialog from "@/components/admin/LineItemsDialog";

export default function RegistrationsTable({ regs, refetch, onEdit, onFollowUp }) {
  const [refundReg, setRefundReg] = useState(null);
  const [issuingCreditFor, setIssuingCreditFor] = useState(null);
  const [consolidateOpen, setConsolidateOpen] = useState(false);
  const [lineItemsReg, setLineItemsReg] = useState(null);
  const markStatus = async (reg, status) => {
    try {
      await api.patch(`/admin/registrations/${reg.id}/classwallet`, {
        classwallet_status: status,
      });
      toast.success(`Marked as ${status}`);
      refetch();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const toggleReferral = async (reg) => {
    const next = (reg.referral_credit_applied || 0) > 0 ? 0 : 10;
    try {
      await api.patch(`/admin/registrations/${reg.id}/referral-credit`, {
        referral_credit_applied: next,
      });
      toast.success(next > 0 ? "$10 referral credit applied" : "Referral credit removed");
      refetch();
    } catch {
      toast.error("Could not update credit");
    }
  };

  const toggleField = async (reg, field, label) => {
    try {
      await api.patch(`/admin/registrations/${reg.id}`, { [field]: !reg[field] });
      toast.success(`${label} ${!reg[field] ? "✓" : "cleared"}`);
      refetch();
    } catch {
      toast.error("Update failed");
    }
  };

  const archive = async (reg) => {
    if (!window.confirm(`Archive registration for ${reg.student_name}?`)) return;
    try {
      await api.patch(`/admin/registrations/${reg.id}`, { archived: true });
      toast.success("Archived");
      refetch();
    } catch {
      toast.error("Archive failed");
    }
  };

  const restore = async (reg) => {
    try {
      await api.patch(`/admin/registrations/${reg.id}`, { archived: false });
      toast.success(`Restored invoice #${reg.invoice_number}`);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const [showArchived, setShowArchived] = useState(false);
  const visibleRegs = showArchived ? regs : regs.filter((r) => !r.archived);
  const archivedCount = regs.filter((r) => r.archived).length;

  if (regs.length === 0) {
    return (
      <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
        No active registrations.
      </div>
    );
  }

  if (visibleRegs.length === 0 && !showArchived && archivedCount > 0) {
    return (
      <div className="rounded-2xl border border-gg-border bg-gg-surface p-12 text-center text-gg-muted">
        No active registrations. {archivedCount} archived — 
        <button type="button" onClick={() => setShowArchived(true)} className="text-gg-green hover:underline ml-1" data-testid="show-archived-empty-btn">
          show archived
        </button>
      </div>
    );
  }

  const exportCsv = () => {
    const headers = [
      "invoice_number", "student_name", "parent_name", "parent_email", "parent_phone",
      "class_title", "class_date", "esa_contract_number", "payment_method",
      "classwallet_status", "parent_payment_status", "refund_status", "refund_amount",
      "discount_code", "discount_amount", "price_student", "price_parent",
      "parent_count", "allergies_notes", "admin_notes", "created_at",
    ];
    const rows = regs.map((r) => [
      r.invoice_number, r.student_name, r.parent_name, r.parent_email, r.parent_phone,
      r.class_title, r.class_date || "", r.esa_contract_number, r.payment_method,
      r.classwallet_status, r.parent_payment_status, r.refund_status || "", r.refund_amount || "",
      r.discount_code || "", r.discount_amount || "", r.price_student, r.price_parent,
      r.parent_count ?? (r.parent_attending ? 1 : 0), r.allergies_notes || "", r.admin_notes || "", r.created_at,
    ]);
    downloadCsv("registrations", headers, rows);
  };

  return (
    <div>
      <div className="flex justify-between items-center gap-2 mb-4">
        <label className="flex items-center gap-2 text-sm text-gg-muted cursor-pointer" data-testid="admin-regs-show-archived-label">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="admin-regs-show-archived"
          />
          Show archived {archivedCount > 0 && <span className="text-[#9b6a26]">({archivedCount})</span>}
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => setConsolidateOpen(true)}
            className="btn-pill btn-outline"
            type="button"
            data-testid="admin-regs-consolidate-btn"
          >
            <Layers size={14} /> Combine invoices
          </button>
          <button
            onClick={exportCsv}
            className="btn-pill btn-outline"
            type="button"
            data-testid="admin-regs-export-csv"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>
    <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
            <tr>
              <Th>Student</Th>
              <Th>Parent</Th>
              <Th>Class</Th>
              <Th>ESA #</Th>
              <Th>Add-on / Curriculum</Th>
              <Th>CW / Referral</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {visibleRegs.map((r) => (
              <tr
                key={r.id}
                className="border-t border-gg-border align-top"
                data-testid={ADMIN.registrationRow(r.id)}
              >
                <Td>
                  <div className="font-semibold">{r.student_name}</div>
                  <div className="text-xs text-gg-muted">#{r.invoice_number}</div>
                  {r.allergies_notes && (
                    <div className="text-[11px] text-[#B24C4C] mt-1 font-semibold max-w-[180px] line-clamp-2">⚠ {r.allergies_notes}</div>
                  )}
                  {r.admin_notes && (
                    <div className="text-[11px] text-gg-brown mt-1 italic max-w-[180px] line-clamp-2">📝 {r.admin_notes}</div>
                  )}
                </Td>
                <Td>
                  <div>{r.parent_name}</div>
                  <div className="text-xs text-gg-muted">{r.parent_email}</div>
                  <div className="text-xs text-gg-muted">{r.parent_phone}</div>
                  {(r.available_credit_balance || 0) > 0 && (
                    <div
                      className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-gg-green/15 text-gg-green font-semibold"
                      title="This parent has an available credit balance"
                      data-testid={`credit-balance-badge-${r.id}`}
                    >
                      <Gift size={10} /> {formatPrice(r.available_credit_balance)} credit
                    </div>
                  )}
                </Td>
                <Td>
                  <div className="max-w-[180px] truncate">{r.class_title}</div>
                  <div className="text-xs text-gg-muted">{formatDate(r.class_date_iso)}</div>
                </Td>
                <Td className="font-mono text-xs">{r.esa_contract_number || "—"}</Td>
                <Td>
                  <div className="space-y-1.5">
                    {r.parent_attending && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleField(r, "add_on_delivered", "Add-on delivered")}
                          className={`text-[11px] px-2 py-0.5 rounded-full ${r.add_on_delivered ? "bg-gg-green text-white" : "bg-gg-bg border border-gg-border text-gg-brown"}`}
                          data-testid={`admin-addon-toggle-${r.id}`}
                        >
                          {r.add_on_delivered ? "✓ Add-on delivered" : "Add-on pending"}
                        </button>
                        <span className={`text-[10px] ${r.parent_payment_status === "paid" ? "text-gg-green" : "text-[#9b6a26]"}`}>
                          {r.parent_payment_status === "paid" ? "CC paid" : "CC pending"}
                        </span>
                      </div>
                    )}
                  </div>
                </Td>
                <Td>
                  <div className="space-y-2">
                    <CwStatusBadge reg={r} />
                    <button
                      type="button"
                      onClick={() => toggleReferral(r)}
                      className={`text-[11px] px-2 py-0.5 rounded-full block ${
                        (r.referral_credit_applied || 0) > 0
                          ? "bg-gg-green text-white"
                          : "bg-gg-bg border border-gg-border text-gg-brown"
                      }`}
                      data-testid={`admin-referral-toggle-${r.id}`}
                    >
                      {(r.referral_credit_applied || 0) > 0 ? "✓ -$10 credit" : "Apply -$10"}
                    </button>
                  </div>
                </Td>
                <Td>
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => onEdit(r)}
                      className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border hover:border-gg-green hover:text-gg-green"
                      data-testid={`admin-edit-reg-${r.id}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/admin/registrations/${r.id}/invoice-url`);
                          window.open(`${API}${data.url}`, "_blank", "noopener");
                        } catch {
                          toast.error("Could not open invoice");
                        }
                      }}
                      className="text-xs px-3 py-1 rounded-full bg-gg-brown/10 text-gg-brown hover:bg-gg-brown hover:text-white transition-colors"
                      data-testid={`admin-view-invoice-${r.id}`}
                    >
                      View Invoice
                    </button>
                    {(r.is_recurring_invoice || r.recurring_pattern_id) && !r.consolidated_into_registration_id && (
                      <>
                        <button
                          type="button"
                          onClick={() => setLineItemsReg(r)}
                          className="text-xs px-3 py-1 rounded-full bg-gg-brown/10 text-gg-brown hover:bg-gg-brown hover:text-white transition-colors flex items-center gap-1"
                          data-testid={`admin-add-line-item-${r.id}`}
                          title="Append a custom charge (e.g. supplies fee, discount) to this invoice"
                        >
                          <Plus size={11} /> Add line item
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Refresh invoice #${r.invoice_number} from its recurring pattern?\n\nThis re-syncs the curriculum, mailing address, session time, and Bill-To fields from the pattern's current values. The invoice number and creation date stay the same. The next PDF download will show the updated data.`)) return;
                            try {
                              const { data } = await api.post(`/admin/registrations/${r.id}/refresh-from-pattern`);
                              toast.success(`Invoice #${data.invoice_number} refreshed — reopen the PDF to see the changes`);
                              refetch();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Refresh failed");
                            }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors flex items-center gap-1"
                          data-testid={`admin-refresh-recurring-${r.id}`}
                          title="Re-sync curriculum, address, and session time from the recurring pattern"
                        >
                          <RefreshCw size={11} /> Regenerate PDF
                        </button>
                      </>
                    )}
                    {r.payment_method !== "direct" && r.classwallet_status !== "submitted" && (
                      <button
                        type="button"
                        onClick={() => markStatus(r, "submitted")}
                        className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors"
                        data-testid={ADMIN.markClasswalletBtn(r.id)}
                      >
                        Mark CW Submitted
                      </button>
                    )}
                    {r.payment_method !== "direct" && r.classwallet_status !== "submitted" && (
                      <>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!confirm(`Convert ${r.student_name}'s registration from ClassWallet to Direct Pay (credit card)?\n\nThis generates a Stripe Checkout link and emails it to ${r.parent_email}. The ClassWallet status will be marked expired.`)) return;
                            try {
                              const { data } = await api.post(`/admin/registrations/${r.id}/convert-to-direct-pay`, {
                                generate_stripe_link: true,
                                notify_parent: true,
                              });
                              if (data.stripe_checkout_url) {
                                await navigator.clipboard?.writeText(data.stripe_checkout_url).catch(() => {});
                                toast.success(`Converted to Direct Pay — Stripe link emailed & copied to clipboard`);
                              } else {
                                toast.success("Converted to Direct Pay");
                              }
                              refetch();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Convert failed");
                            }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-gg-brown/10 text-gg-brown hover:bg-gg-brown hover:text-white transition-colors"
                          data-testid={`admin-convert-direct-${r.id}`}
                          title="Switch this registration to credit-card payment and generate a Stripe link"
                        >
                          → Direct Pay
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const ref = window.prompt(
                              `Mark ${r.student_name}'s registration as PAID via external means (Zelle, cash, manual Stripe charge, etc.)?\n\nOptional reference (logged for accounting):`,
                              ""
                            );
                            if (ref === null) return;
                            try {
                              await api.post(`/admin/registrations/${r.id}/mark-paid-manually`, {
                                payment_reference: ref || "",
                                notify_parent: false,
                              });
                              toast.success(`Marked paid — ${r.student_name}`);
                              refetch();
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Could not mark paid");
                            }
                          }}
                          className="text-xs px-3 py-1 rounded-full bg-gg-green text-white hover:bg-gg-green/90 transition-colors"
                          data-testid={`admin-mark-paid-manual-${r.id}`}
                          title="Already collected payment outside the system? Mark fully paid (no Stripe)."
                        >
                          ✓ Mark Paid
                        </button>
                      </>
                    )}
                    {r.parent_payment_status !== "paid" && (r.payment_method === "direct" || r.parent_attending || (r.parent_count || 0) > 0) && (
                      <button
                        type="button"
                        onClick={async () => {
                          const what = r.payment_method === "direct" ? "this Direct-Pay registration" : "the parent add-on charge";
                          if (!confirm(`Mark ${what} for ${r.student_name} as PAID via Stripe?`)) return;
                          try {
                            await api.post(`/admin/registrations/${r.id}/mark-stripe-paid`, { notify_parent: true });
                            toast.success("Marked paid + receipt emailed");
                            refetch();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Failed");
                          }
                        }}
                        className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors"
                        data-testid={`admin-mark-stripe-paid-${r.id}`}
                      >
                        Mark Paid (Stripe)
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onFollowUp?.(r)}
                      className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"
                      data-testid={`admin-followup-${r.id}`}
                    >
                      Send Follow-up
                    </button>
                    {r.refund_status !== "refunded" && (
                      <button
                        type="button"
                        onClick={() => setRefundReg(r)}
                        className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-[#B24C4C] hover:text-[#B24C4C] inline-flex items-center gap-1"
                        data-testid={`admin-refund-${r.id}`}
                      >
                        <Undo2 size={11} /> Refund
                      </button>
                    )}
                    {r.refund_status === "refunded" && (
                      <span
                        className="text-[10px] text-[#B24C4C] font-semibold uppercase tracking-[0.14em] text-center"
                        data-testid={`admin-refunded-badge-${r.id}`}
                      >
                        Refunded ${(r.refund_amount || 0).toFixed(2)}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setIssuingCreditFor(r)}
                      className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green inline-flex items-center gap-1"
                      data-testid={`admin-issue-credit-${r.id}`}
                      title="Issue a credit from this registration (no-show, cancellation, make-up)"
                    >
                      <Gift size={11} /> Issue Credit
                    </button>
                    <button
                      type="button"
                      onClick={() => (r.archived ? restore(r) : archive(r))}
                      className={`text-xs px-3 py-1 rounded-full bg-gg-bg border ${r.archived ? "border-gg-green text-gg-green hover:bg-gg-green hover:text-white" : "border-gg-border text-gg-muted hover:text-[#B24C4C] hover:border-[#B24C4C]"}`}
                      data-testid={r.archived ? `admin-restore-reg-${r.id}` : `admin-archive-reg-${r.id}`}
                    >
                      {r.archived ? "Restore" : "Archive"}
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    {refundReg && (
      <RefundDialog
        reg={refundReg}
        onClose={() => setRefundReg(null)}
        onSaved={() => { setRefundReg(null); refetch(); }}
      />
    )}
    {issuingCreditFor && (
      <IssueCreditFromRegDialog
        reg={issuingCreditFor}
        onClose={() => setIssuingCreditFor(null)}
        onSaved={() => { setIssuingCreditFor(null); refetch(); }}
      />
    )}
    {consolidateOpen && (
      <ConsolidateInvoicesDialog
        regs={regs}
        onClose={() => setConsolidateOpen(false)}
        onDone={() => { setConsolidateOpen(false); refetch(); }}
      />
    )}
    {lineItemsReg && (
      <LineItemsDialog
        registration={lineItemsReg}
        onClose={() => setLineItemsReg(null)}
        onSaved={() => { refetch(); }}
      />
    )}
    </div>
  );
}

function IssueCreditFromRegDialog({ reg, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: reg.price_student || 55,
    reason: "no-show",
    expires_in_months: 12,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Amount must be positive");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/registrations/${reg.id}/issue-credit`, {
        parent_email: reg.parent_email,
        parent_name: reg.parent_name,
        student_name: reg.student_name,
        amount: Number(form.amount),
        reason: form.reason,
        expires_in_months: form.expires_in_months ? Number(form.expires_in_months) : 0,
        notes: form.notes,
      });
      toast.success(`Issued ${formatPrice(Number(form.amount))} credit for ${reg.parent_name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-gg-surface">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Issue credit</DialogTitle>
          <DialogDescription>
            For <strong>{reg.parent_name}</strong> ({reg.parent_email}). Auto-linked to {reg.class_title}.
            Once issued, this credit can be applied to any future registration for this parent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount ($)</Label>
            <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5" data-testid="issue-from-reg-amount" />
          </div>
          <div>
            <Label>Reason</Label>
            <select
              value={form.reason}
              onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
              data-testid="issue-from-reg-reason"
            >
              <option value="no-show">No-show (student couldn&apos;t attend)</option>
              <option value="class-canceled">Class canceled</option>
              <option value="goodwill">Goodwill</option>
              <option value="make-up">Make-up session</option>
              <option value="refund-as-credit">Refund as credit (instead of cash)</option>
            </select>
          </div>
          <div>
            <Label>Expires in (months, 0 = never)</Label>
            <Input type="number" min="0" value={form.expires_in_months} onChange={(e) => setForm((f) => ({ ...f, expires_in_months: e.target.value }))} className="mt-1.5" />
          </div>
          <div>
            <Label>Notes (private)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1.5" placeholder="Any context you want to remember later…" data-testid="issue-from-reg-notes" />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="issue-from-reg-save">
            {busy ? "Issuing…" : "Issue credit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
